if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.closest('product-info')?.hasAttribute('data-pdp-selection-pending')) return;
        if (this.isSubmitting || this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.isSubmitting = true;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const formData = new FormData(this.form);
        const companionVariantId = String(formData.get("b1_companion_variant_id") || "").trim();
        const companionPropertiesRaw = String(formData.get("b1_companion_properties_json") || "").trim();
        let companionProperties = {};
        if (companionPropertiesRaw) {
          try {
            const parsed = JSON.parse(companionPropertiesRaw);
            if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) companionProperties = parsed;
          } catch (companionPropertiesError) {
            console.warn("[B1 Set] Unable to parse companion line properties.", companionPropertiesError);
          }
        }
        formData.delete("b1_companion_variant_id");
        formData.delete("b1_companion_properties_json");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        if (companionVariantId) config.headers["Accept"] = "application/json";

        if (this.cart) this.cart.setActiveElement(document.activeElement);

        if (companionVariantId) {
          const quantity = Math.max(1, Number.parseInt(formData.get("quantity") || "1", 10) || 1);
          const mainItem = {
            id: Number(formData.get("id")),
            quantity,
          };

          const properties = {};
          formData.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match && String(value).length) properties[match[1]] = value;
          });

          // A B¹ set id identifies one physical configured purchase, not one PDP session.
          // Generate it at submission time so two configurations added from the same
          // BandWare page can never be mistaken for one another in the cart.
          if (properties._b1_set_id || companionProperties._b1_set_id) {
            const submissionSetId = `b1-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
            properties._b1_set_id = submissionSetId;
            companionProperties._b1_set_id = submissionSetId;
          }

          if (Object.keys(properties).length) mainItem.properties = properties;

          const sellingPlan = formData.get("selling_plan");
          if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);

          const companionItem = { id: Number(companionVariantId), quantity };
          if (Object.keys(companionProperties).length) companionItem.properties = companionProperties;

          const payload = {
            items: [
              mainItem,
              companionItem,
            ],
          };

          if (this.cart) {
            payload.sections = this.cart
              .getSectionsToRender()
              .map((section) => section.id)
              .join(",");
            payload.sections_url = window.location.pathname;
          }

          config.body = JSON.stringify(payload);
        } else {
          delete config.headers["Content-Type"];
          if (this.cart) {
            formData.append(
              "sections",
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append("sections_url", window.location.pathname);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (companionVariantId && !response.id) response.id = Number(formData.get("id"));
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const renderCartContents = () => {
              const quickAddModal = this.closest("quick-add-modal");
              if (quickAddModal) {
                document.body.addEventListener(
                  "modalClosed",
                  () => {
                    setTimeout(() => {
                      this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                this.cart.renderContents(response);
              }
            };

            if (!this.error && companionVariantId) {
              // The add response already contains the two rendered cart sections.
              // Show those immediately, then read the canonical cart state once for
              // analytics and quantity subscribers without asking the drawer to
              // render a second time.
              renderCartContents();

              let confirmedCartData = response;
              try {
                const cartResponse = await fetch(`${routes.cart_url}.js`, {
                  headers: { Accept: "application/json" },
                });
                if (cartResponse.ok) confirmedCartData = await cartResponse.json();
              } catch (cartStateError) {
                console.warn("[B1 Set] Unable to read confirmed cart state.", cartStateError);
              }

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: confirmedCartData,
                skipCartSectionRefresh: true,
              });
            } else {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: "product-form",
                  productVariantId: formData.get("id"),
                  cartData: response,
                });

              renderCartContents();
            }
            this.error = false;
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage("Unable to add these pieces. Please try again.");
          })
          .finally(() => {
            this.isSubmitting = false;
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (this.closest('product-info')?.hasAttribute('data-pdp-selection-pending')) {
          this.pdpPendingSubmit = { disable, text };
          disable = true;
          // An unresolved selection must not conceal Shopify's product-wide stock state.
          text = this.dataset.productAvailable === 'false'
            ? window.variantStrings.soldOut
            : 'Select options';
        }
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          if (text && text !== 'Sold out') {
            this.submitButtonText.textContent = text;
          } else {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}

/*
document.addEventListener('DOMContentLoaded', () => {
  syncSitbTextFromATC();
});

subscribe(PUB_SUB_EVENTS.variantChange, () => {
  // Shopify ko pehle button update karne do
  setTimeout(() => {
    syncSitbTextFromATC();
  }, 0);
});*/




function syncSitbTextFromATC() {
  const sitb = document.querySelector('.sitb');
  const customButton = document.getElementById('custom-add-to-cart');
  
  if (!sitb) return; // 
    

  const textNode = sitb.querySelector('.sitb-text') || sitb;

  // Hidden real Add to Cart button span
  const atcSpan = document.querySelector(
    'product-form button[type="submit"] span'
  );
  if (!atcSpan) return;

  const atcText = atcSpan.textContent.trim().toLowerCase();

  //  FINAL LOGIC
  if (atcText.includes('sold')) {
    
    
    textNode.textContent = 'Sold out';
    sitb.setAttribute('disabled', 'disabled');
    sitb.classList.add('is-disabled');

    sitb.style.pointerEvents = 'none';
     customButton.setAttribute('disabled', 'disabled');
    
  } else if (atcText.includes('preorder')) {
    textNode.textContent = 'Preorder';
    sitb.removeAttribute('disabled', 'disabled');
       customButton.removeAttribute('disabled');
   sitb.classList.remove('is-disabled');
       //  sitb.classList.add('is-disabled');
    sitb.style.pointerEvents = '';
    
  } else {
    // DEFAULT CASE
    textNode.textContent = 'Sign In to Buy';
    sitb.classList.remove('is-disabled');
     sitb.removeAttribute('disabled', 'disabled');
       customButton.removeAttribute('disabled');
    sitb.style.pointerEvents = '';
  }
}


